import axios from "axios";
import { useAuth } from "../utils/userAuth";
import { useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useQueryParam } from "../utils/url";
import { getServiceUrl } from "../utils/apiClient";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { MicrosoftLoginButton } from "./MicrosoftLoginButton";
import { CredentialsAuthClientNonceResponse, CredentialsAuthClientNonceBody } from "@services/credentials/types";

export type SSOLoginType = "microsoft" | "google";

export interface SSOStateUriComponent {
  redirectUrl?: string;
  ssoType: SSOLoginType;
}

const LogoutButton = ({ onLogout }: { onLogout: () => void }) => {
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${getServiceUrl()}/credentials/logout`,
        {},
        { withCredentials: true }
      );
      console.log("Logout successful");
      onLogout();
    } catch (error: any) {
      console.error("Logout failed:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={handleLogout}
      disabled={loading}
      fullWidth
    >
      {loading ? <CircularProgress size={24} /> : "Logout"}
    </Button>
  );
};

export const UserLoginPage = () => {
  const [loginType, setLoginType] = useState<SSOLoginType | undefined>();
  const code = useQueryParam("code");
  const stateQueryComponent = useQueryParam("state");
  const redirectUrl = useQueryParam("redirectUrl");
  const clientNonce = useQueryParam("client_nonce");
  const ssoTypeParam = useQueryParam("ssoType") as SSOLoginType | null;
  const [loginFailedMessage, setLoginFailedMessage] = useState<string>("");
  const [step, setStep] = useState<1 | 2>(code ? 2 : 1);
  const [oauthCode, setOauthCode] = useState<string | null>(null);
  const { isLoggedIn, checkAuth } = useAuth((isLoggedInCheck) => {
    setOauthCode(code);

    if (isLoggedInCheck && clientNonce) {
      // verify nonce
      axios.post<CredentialsAuthClientNonceResponse>(
        `${getServiceUrl()}/credentials/auth_client_nonce`,
        { nonce: clientNonce } satisfies CredentialsAuthClientNonceBody,
        { withCredentials: true }
      );
    }
    // possibly redirect
    if (isLoggedInCheck && redirectUrl) {
      window.location.href = redirectUrl;
    }
  });
  const {
    redirectUrl: redirectUrlFromState,
    ssoType,
    clientNonce: clientNonceFromState,
  } = stateQueryComponent
    ? JSON.parse(decodeURIComponent(stateQueryComponent))
    : { redirectUrl: null, ssoType: null, clientNonce: null };

  const onLoginStart = (type: SSOLoginType) => {
    setLoginType(type);
  };

  const onLoginSuccess = (stateUriComponent: string) => {
    checkAuth();
    setLoginFailedMessage("");
    if (redirectUrlFromState) {
      window.location.href = redirectUrlFromState;
    }
  };

  return (
    <Container maxWidth={step === 2 ? "md" : "xs"}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Paper
          elevation={3}
          sx={{ padding: 4, textAlign: "center", width: "100%" }}
        >
          {/* Back Button (Only on Step 2) */}
          {step === 2 && (
            <Button
              onClick={() => setStep(1)}
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 2, alignSelf: "flex-start" }}
              size="small"
            >
              Back
            </Button>
          )}

          <Typography variant="h5" fontWeight="bold" mb={2}>
            {isLoggedIn ? "Everything set! 🎉" : "Set up Dobby"}
          </Typography>

          {loginFailedMessage && (
            <Typography color="error" mb={2}>
              {loginFailedMessage}
            </Typography>
          )}

          {/* Step 1: Start Setup */}
          {!isLoggedIn && step === 1 && (
            <Box>
              <Box mb={2}>
                <img
                  src="/www/assets/dobby_login_hero.png"
                  alt="Login Hero"
                  style={{
                    width: "100%",
                    maxHeight: "200px",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                We will need you to connect your work account and give it the
                necessary permissions.
              </Typography>
              <Button
                variant="contained"
                sx={{ maxWidth: "120px" }}
                color="primary"
                endIcon={<ArrowForwardIosIcon />}
                onClick={() => setStep(2)}
                fullWidth
              >
                Start
              </Button>
            </Box>
          )}

          {/* Step 2: Instructions + Login */}
          {!isLoggedIn && step === 2 && (
            <Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {ssoTypeParam === "microsoft" 
                  ? "Click on \"Continue With Microsoft\" button below to set up your account."
                  : "Click on \"Continue With Google\" button below and follow the instructions to set up your account."
                }
              </Typography>
              
              {/* Show instructions only for Google or when no specific ssoType is specified */}
              {(!ssoTypeParam || ssoTypeParam === "google") && (
                <Box display="flex" flexDirection="row" sx={{ gap: "4px" }}>
                  {/* Box 1- Bypass Google Warning */}
                  <Box
                    flex={1}
                    p={2}
                    mb={2}
                    sx={{ backgroundColor: "#eff4f8", borderRadius: 2 }}
                  >
                    <Typography fontWeight="bold" mb={1}>
                      Bypass "Google hasn't verified this app"
                    </Typography>
                    <img
                      src="/www/assets/google_unverified_instructions.gif"
                      alt="Google Unverified Instructions"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "contain",
                      }}
                    />
                  </Box>

                  {/* Step 4 - Grant Permissions */}
                  <Box
                    flex={1}
                    p={2}
                    mb={2}
                    sx={{ backgroundColor: "#eff4f8", borderRadius: 2 }}
                  >
                    <Typography fontWeight="bold" mb={1}>
                      Grant Dobby the necessary permissions
                    </Typography>
                    <img
                      src="/www/assets/google_grant_permission_instructions.gif"
                      alt="Google Grant Permissions"
                      style={{
                        width: "100%",
                        maxHeight: "300px",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Show Google login button only if ssoType is not specified or is google */}
              {(!ssoTypeParam || ssoTypeParam === "google") && (
                <Box mt={2}>
                  <GoogleLoginButton
                    redirectUrl={redirectUrl}
                    code={ssoType === "google" ? oauthCode : null}
                    onLoginStart={onLoginStart}
                    onSuccess={onLoginSuccess}
                    onFailure={setLoginFailedMessage}
                    connectNewInbox={false}
                    clientNonce={clientNonce || clientNonceFromState || undefined}
                  />
                </Box>
              )}
              
              {/* Show Microsoft login button only if ssoType is not specified or is microsoft */}
              {(!ssoTypeParam || ssoTypeParam === "microsoft") && (
                <Box mt={2}>
                  <MicrosoftLoginButton
                    redirectUrl={redirectUrl}
                    code={ssoType === "microsoft" ? oauthCode : null}
                    onLoginStart={onLoginStart}
                    onSuccess={onLoginSuccess}
                    onFailure={setLoginFailedMessage}
                    connectNewInbox={false}
                    clientNonce={clientNonce || clientNonceFromState || undefined}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Logged-in View */}
          {isLoggedIn && (
            <>
              <Typography variant="body1" mt={2} mb={2}>
                You're logged in. Nothing else to do right now, thank you!
              </Typography>
              <LogoutButton onLogout={checkAuth} />
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};
